/**
 * Fixtures list for the League Detail Fixtures tab.
 * Time-ordered (server), segmented with a group/round banner whenever the round
 * changes, and paginated via a "show more" affordance. Reuses ScoreboardMatchRow
 * so rows match the Scoreboard screen visually.
 */

import React, { useCallback, useMemo } from 'react'
import { ActivityIndicator, Platform, Pressable, Text, View, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { useTheme } from '../../../src/theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { ScoreboardMatchRow } from '../../gameResults/ScoreboardMatchRow/ScoreboardMatchRow'
import {
  buildLeagueFixtureSections,
  type LeagueFixture,
} from '../../../src/leagueDetail/helpers/leagueFixtureSections.helper'
import { getLeagueFixturesListStyles } from './LeagueFixturesList.styles'
import { LEAGUE_FIXTURES_COPY, LEAGUE_FIXTURES_TEST_IDS } from './LeagueFixturesList.constants'

export interface LeagueFixturesListProps {
  matches: LeagueFixture[]
  isLoading: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onLoadMore?: () => void
  returnToPath?: string
}

export const LeagueFixturesList: React.FC<LeagueFixturesListProps> = ({
  matches,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  returnToPath,
}) => {
  const theme = useTheme()
  const router = useRouter()
  const { width: windowWidth } = useWindowDimensions()
  const showWebHover = Platform.OS === 'web'
  const styles = useMemo(
    () => getLeagueFixturesListStyles(theme, windowWidth),
    [theme, windowWidth],
  )

  const sections = useMemo(() => buildLeagueFixtureSections(matches), [matches])

  const openMatchHub = useCallback(
    (gameId: string) => {
      router.push({
        pathname: '/app/match/[gameId]',
        params: { gameId, returnTo: returnToPath ?? '/app/gameResults' },
      })
    },
    [returnToPath, router],
  )

  if (matches.length === 0) {
    return (
      <View style={styles.card}>
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyText}>
            {isLoading ? LEAGUE_FIXTURES_COPY.LOADING : LEAGUE_FIXTURES_COPY.EMPTY}
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.card}>
      {sections.map((item, index) => {
        if (item.type === 'banner') {
          return (
            <View key={item.key} style={styles.banner} testID={LEAGUE_FIXTURES_TEST_IDS.banner}>
              <Text style={styles.bannerText}>{item.label}</Text>
            </View>
          )
        }
        // Divider between consecutive games; never directly under a banner or on the first row.
        const showSeparator = index > 0 && sections[index - 1].type === 'fixture'
        return (
          <React.Fragment key={item.key}>
            {showSeparator ? <View style={styles.rowSeparator} /> : null}
            <ScoreboardMatchRow
              game={item.fixture}
              isLast
              testID={`league-fixture-${item.fixture.id}`}
              onPress={(g) => {
                openMatchHub(g.id)
              }}
            />
          </React.Fragment>
        )
      })}

      {hasNextPage && onLoadMore ? (
        <Pressable
          onPress={onLoadMore}
          disabled={isFetchingNextPage}
          accessibilityRole="button"
          testID={LEAGUE_FIXTURES_TEST_IDS.loadMore}
          style={({ pressed, hovered }) => [
            styles.loadMore,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.loadMorePressed,
            hovered && showWebHover && styles.loadMoreHover,
          ]}
        >
          {isFetchingNextPage ? (
            <ActivityIndicator size="small" color={theme.primary} />
          ) : (
            <Text style={styles.loadMoreText}>{LEAGUE_FIXTURES_COPY.LOAD_MORE}</Text>
          )}
        </Pressable>
      ) : null}
    </View>
  )
}
